import { base64ToFile } from '../../../services/DeliveryDataService';

/**
 * Object URL 管理器
 * 统一管理所有 Object URL 的创建和释放，防止内存泄漏
 */
export class ObjectURLManager {
  private urls = new Set<string>();

  /**
   * 从 Blob 创建 Object URL 并记录
   * @param blob Blob 对象
   * @returns Object URL 字符串
   */
  create(blob: Blob): string {
    const url = URL.createObjectURL(blob);
    this.urls.add(url);
    return url;
  }

  /**
   * 从 File 创建 Object URL 并记录
   * @param file File 对象
   * @returns Object URL 字符串
   */
  createFromFile(file: File): string {
    return this.create(file);
  }

  /**
   * 释放单个 Object URL
   * @param url 要释放的 URL
   */
  revoke(url: string): void {
    if (this.urls.has(url)) {
      URL.revokeObjectURL(url);
      this.urls.delete(url);
    }
  }

  /**
   * 释放所有已创建的 Object URL
   * 通常在组件卸载时调用
   */
  revokeAll(): void {
    this.urls.forEach(url => URL.revokeObjectURL(url));
    this.urls.clear();
  }

  /**
   * 获取当前管理的 URL 数量
   */
  get size(): number {
    return this.urls.size;
  }
}

/**
 * Base64 字符串转 Object URL
 * @param base64 Base64 字符串（带或不带 data:image 前缀）
 * @param filename 文件名（可选）
 * @returns Promise<string> Object URL
 */
export async function base64ToObjectURL(
  base64: string,
  filename: string = `photo_${Date.now()}.jpg`
): Promise<string> {
  try {
    // 复用项目已有的 base64ToFile 工具
    const file = await base64ToFile(base64, filename);
    
    // 创建 Blob（避免 File 对象的额外元数据）
    const blob = file.slice(0, file.size, 'image/jpeg');
    
    // 返回 Object URL
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('[imageOptimizationUtils] Base64 转 ObjectURL 失败:', error);
    throw error;
  }
}

/**
 * Blob 转 Base64（用于全屏预览时的反向转换）
 * @param blob Blob 对象
 * @returns Promise<string> Base64 字符串
 */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * LRU 缓存类
 * 用于缓存最近使用的图片，自动淘汰最旧的
 */
export class LRUCache<K, V> {
  private cache = new Map<K, V>();
  private readonly maxSize: number;

  constructor(maxSize: number = 10) {
    this.maxSize = maxSize;
  }

  /**
   * 获取缓存值（会更新访问顺序）
   */
  get(key: K): V | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // 重新插入以更新顺序（Map 保持插入顺序）
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  /**
   * 设置缓存值（超出容量时淘汰最旧的）
   */
  set(key: K, value: V): void {
    // 如果已存在，先删除（以更新顺序）
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }
    
    // 如果超出容量，删除最旧的（Map 的第一个元素）
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(key, value);
  }

  /**
   * 检查键是否存在
   */
  has(key: K): boolean {
    return this.cache.get(key) !== undefined;
  }

  /**
   * 清空缓存
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * 获取缓存大小
   */
  get size(): number {
    return this.cache.size;
  }
}
